import { useState } from "react";
import { Box, Paper, Title, Text, TextInput, PasswordInput, Button, Divider, Anchor, Flex } from "@mantine/core";
import { IconMail, IconLock } from "@tabler/icons-react";
import { Link, useNavigate } from "react-router-dom";
import trpc, { saveToken } from "../../shared/utils/trpc";
import { handleTrpcError } from "../../shared/utils/trpc/handletrpcError.util.ts";
import { successNotification } from "../../shared/utils/successNotification";
import { useUser } from "@/context/UserContext";
import { jwtDecode } from "jwt-decode";
import Cookies from "js-cookie";

function LoginPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { refetchUser } = useUser();

  const token = Cookies.get("token");
  if (token) {
    try {
      const decoded = jwtDecode<{ role: string; exp: number }>(token);
      if (decoded.exp > Date.now() / 1000) {
        if (decoded.role === "ADMIN") {
          navigate("/admin/companies", { replace: true });
          return null;
        }
        navigate("/profile", { replace: true });
        return null;
      }
      Cookies.remove("token");
    } catch {
      Cookies.remove("token");
    }
  }
  /* ---------------- Validation ---------------- */

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const isPasswordValid = password.length >= 8;

  const isFormValid = email.trim() !== "" && password.trim() !== "" && isValidEmail(email) && isPasswordValid;

  /* ---------------- Submit ---------------- */

  const handleLogin = async () => {
    if (!isFormValid) return;

    setLoading(true);

    try {
      const data = await trpc.auth.login.mutate({
        email,
        password,
      });

      // Save token to cookies
      if (data.token) {
        saveToken(data.token);
        refetchUser();
      }

      successNotification({
        title: "Login Successful!",
        message: "You have successfully logged in.",
      });

      navigate("/create-profile", { replace: true });
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Flex direction="column" align="center" gap="md">
        {/* Header */}
        <Flex direction="column" align="center" mb="sm">
          <img src="/gtLogo.svg" alt="GT Setu" style={{ height: 64, width: "auto" }} />
        </Flex>

        {/* Login Card */}
        <Paper shadow="md" p="xl" radius="lg" style={{ width: 360 }}>
          <Title order={2} mb="xs">
            Login
          </Title>
          <Text size="sm" c="slate.6" mb="lg">
            Welcome back! Please sign in to your account.
          </Text>

          <TextInput
            label="Email address"
            placeholder="you@example.com"
            required
            mb="md"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            leftSection={<IconMail size={18} color="#6B7280" />}
            error={email && !isValidEmail(email) ? "Please enter a valid email address" : null}
          />

          <PasswordInput
            label="Password"
            placeholder="Your password"
            required
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            leftSection={<IconLock size={18} color="#6B7280" />}
            error={password && !isPasswordValid ? "Password must be at least 8 characters" : null}
          />

          <Flex justify="flex-end" mt={6} mb="md">
            <Anchor size="sm" component={Link} to="/forgot-password">
              Forgot password?
            </Anchor>
          </Flex>

          {/* Login Button */}
          <Button fullWidth mb="md" onClick={handleLogin} loading={loading} disabled={!isFormValid || loading}>
            Login
          </Button>

          <Divider label="New to GT?" labelPosition="center" my="md" />

          <Button fullWidth variant="outline" component={Link} to="/signup">
            Create account
          </Button>
        </Paper>

        <Anchor component={Link} to="/home" size="sm" c="slate.6">
          ← Back to home
        </Anchor>
      </Flex>
    </Box>
  );
}

export default LoginPage;
